import React from "react";
import { motion } from "framer-motion";
import { Upload, Lock, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Button } from "@/components/ui/button";
import { JOURNEY } from "@/data/content";
import { useBooking } from "@/context/BookingContext";

const LINE_FROM = { width: 0 };
const LINE_TO = { width: "100%" };
const LINE_VIEWPORT = { once: true };
const LINE_TRANSITION = { duration: 1.6, ease: [0.16, 1, 0.3, 1] };

const PatientJourney = () => {
  const { openBooking } = useBooking();
  return (
    <section data-testid="patient-journey-timeline" className="relative bg-white py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">05 — Patient Journey</p>
          <h2 className="font-heading font-extrabold text-[#0b2c4d] text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
            A clear path from first call to full recovery
          </h2>
        </Reveal>

        {/* Horizontal timeline */}
        <div className="relative">
          <div className="absolute left-0 right-0 top-8 h-px bg-slate-200 hidden lg:block" />
          <motion.div
            className="absolute left-0 top-8 h-px bg-brand hidden lg:block"
            initial={LINE_FROM}
            whileInView={LINE_TO}
            viewport={LINE_VIEWPORT}
            transition={LINE_TRANSITION}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-6 lg:gap-4">
            {JOURNEY.map((j, i) => (
              <Reveal key={j.step} delay={i * 0.1} className="relative">
                <div className="w-16 h-16 rounded-full border-2 border-brand/25 flex items-center justify-center mb-4 relative z-10 bg-white shadow-sm">
                  <span className="font-heading font-extrabold text-brand text-lg">{j.step}</span>
                </div>
                <h3 className="font-heading font-extrabold text-[#0b2c4d] text-lg tracking-tight">{j.title}</h3>
                <p className="text-slate-500 text-sm mt-1.5 leading-relaxed">{j.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Upload MRI CTA */}
        <Reveal delay={0.2} className="mt-16">
          <div className="rounded-[2rem] p-8 md:p-12 relative overflow-hidden bg-brand-light border border-brand/15">
            <div className="absolute -right-10 -top-10 w-52 h-52 rounded-full bg-brand/10 blur-3xl" />
            <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div className="max-w-xl">
                <div className="w-14 h-14 rounded-2xl bg-brand flex items-center justify-center mb-5 shadow-lg shadow-brand/25">
                  <Upload className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-heading font-extrabold text-[#0b2c4d] text-2xl md:text-3xl tracking-tight">
                  Send your MRI for a specialist review
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  Share your MRI and reports securely for review by Dr. Reddy. Our team will guide
                  you through the next steps.
                </p>
                <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
                  <Lock className="w-4 h-4 text-brand" />
                  Your data is kept private and confidential, used only for your clinical care.
                </div>
              </div>
              <Button
                onClick={() => openBooking("mri_review")}
                data-testid="upload-mri-btn"
                className="bg-brand hover:bg-brand-hover text-white font-semibold px-7 py-6 rounded-full group shrink-0 shadow-lg shadow-brand/25"
              >
                Upload MRI Securely
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default PatientJourney;
